import React from 'react';
import { Box, Flex, Textarea, IconButton, Text, HStack, Icon, useDisclosure } from '@chakra-ui/react';
import CustomTooltip from 'components/shared/CustomTooltip';
import { AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useDebouncedCallback } from 'use-debounce';
import { FiSend, FiLock, FiClock, FiMic } from 'react-icons/fi';
import useAppToast from 'components/toast';
import { usePermissions } from 'hooks/usePermissions';
import { useSendMessageMutation } from 'api/metaWhatsappApi';
import { windowState } from './WindowChip';
import { explainRefusal } from './sendErrors';
import SendTemplateButton from './SendTemplateButton';
import QuotedMessage from './QuotedMessage';
import AttachMenu from './AttachMenu';
import AttachmentPreview from './AttachmentPreview';
import VoiceRecorder from './VoiceRecorder';
import { QuickReplyList, QuickReplyManager, matchQuickReplies } from './QuickReplies';
import { MotionBox, MotionIconButton, useMotionPrefs, EASE, slideUp } from './motion';
import { readPrefs, usePrefs, prefs } from '../hooks/useLocalPrefs';
import { emitTyping } from '../hooks/useTyping';
import useAttachment from '../hooks/useAttachment';
import useVoiceRecorder from '../hooks/useVoiceRecorder';
import useOutboundMedia from '../hooks/useOutboundMedia';
import { MEDIA_SEND_ENABLED } from '../featureFlags';

/**
 * The message box, the way WhatsApp draws it: emoji and attach on the left, a
 * rounded field that grows with the text and carries the reply strip, and one
 * round green button that is a microphone until there is text to send.
 *
 * ★ IT NEVER SENDS A SENDER FIELD.
 * The server answers `400 UNEXPECTED_SENDER_FIELD` to a body naming `from` or
 * `phoneNumberId`, deliberately, so a component that tried to choose a number
 * would fail loudly. The reply always goes out on the conversation's bound
 * channel, which is the only correct answer — the customer's thread exists
 * between two specific numbers.
 *
 * ★ THE CLIENT MESSAGE ID IS THE IDEMPOTENCY KEY.
 * Generated once per attempt and reused on retry, so a send that timed out
 * ambiguously returns the original message rather than delivering twice.
 *
 * ★ A REPLY CARRIES `context.messageId` — the connection layer's field, which
 * it maps to Meta's `message_id`. The quote is also remembered per browser so
 * it survives until the server records it.
 *
 * ★ NOTHING HERE ENFORCES ANYTHING. Ownership and the 24-hour window are both
 * re-checked by the server on every send; the states below only spare the
 * agent from typing a reply that was never going to leave.
 */

const MAX_ROWS = 6;
const LINE_PX = 22;

const newClientMessageId = () =>
	`cli_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** A quiet strip in place of the box when nothing can be typed. */
function Blocked({ icon, title, detail, action }) {
	return (
		<Box px="4" py="3" bg="wa.composerBar" borderTopWidth="1px" borderColor="wa.divider">
			<HStack spacing="3" px="4" py="2.5" borderRadius="full" bg="wa.input">
				<Icon as={icon} color="wa.inkMuted" />
				<Box flex="1" minW="0">
					<Text fontSize="sm" color="wa.ink" fontWeight="medium">
						{title}
					</Text>
					<Text fontSize="xs" color="wa.inkMuted">
						{detail}
					</Text>
				</Box>
				{/* ★ A RULE WITH NO WAY OUT READS AS A BUG. Both closed states are
				    correct and both used to end here, leaving the agent nothing to
				    click — so the one message WhatsApp does allow is offered. */}
				{action}
			</HStack>
		</Box>
	);
}

export default function Composer({ conversation, disabled, replyTo, replySender, onCancelReply, numberState = null }) {
	const conversationId = conversation?._id;
	const [body, setBody] = React.useState(() => readPrefs().drafts[conversationId] ?? '');
	const [clientMessageId, setClientMessageId] = React.useState(newClientMessageId);
	const [sendMessage, { isLoading }] = useSendMessageMutation();
	const notify = useAppToast();
	const ref = React.useRef(null);
	const { dur } = useMotionPrefs();
	const { hasPermission } = usePermissions();
	const p = usePrefs();
	const manager = useDisclosure();
	const [qrIndex, setQrIndex] = React.useState(0);

	const { attachment, attach, clear: clearAttachment, preparing } = useAttachment();
	const recorder = useVoiceRecorder();
	const { sendMedia, isSending: sendingMedia } = useOutboundMedia(conversation);

	const { open, everInbound } = windowState(conversation?.lastInboundAt);
	const notMine = Boolean(conversation?.owner) && !conversation?.viewerIsOwner;
	const canType = !disabled && open && !notMine;
	// The server checks this too; hiding the buttons only avoids offering
	// something that would answer 403 — or, until the upload route is live, a
	// button whose only outcome is a toast.
	const canMedia = canType && MEDIA_SEND_ENABLED && hasPermission('whatsapp', 'message.send.media');

	// The emoji picker used to be hidden in the lead drawer, because its grid
	// covered most of the narrow panel. It is gone from the composer entirely
	// now, and the template picker that replaced it is a Modal — which works in
	// the drawer and on the inbox page alike, so there is nothing left to
	// special-case. See portalTarget.jsx for why a Menu would not have.

	// Grow with the text up to a few lines, then scroll — the field never jumps
	// to a fixed tall box on the first keystroke.
	React.useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${Math.min(el.scrollHeight, MAX_ROWS * LINE_PX + 16)}px`;
	}, [body]);

	// Switching thread restores that thread's draft; a reply request focuses
	// the box the way tapping Reply does on the phone.
	React.useEffect(() => {
		setBody(readPrefs().drafts[conversationId] ?? '');
		setClientMessageId(newClientMessageId());
	}, [conversationId]);
	React.useEffect(() => {
		if (replyTo) ref.current?.focus();
	}, [replyTo]);

	const saveDraft = useDebouncedCallback((id, text) => prefs.setDraft(id, text), 300);

	const onChange = (e) => {
		const next = e.target.value;
		setBody(next);
		saveDraft(conversationId, next);
		if (next.trim()) emitTyping(conversationId);
		setQrIndex(0);
	};

	// "/" at the start of the box opens quick replies, filtered by the rest.
	const quickOpen = canType && body.startsWith('/') && !body.includes('\n');
	const quickMatches = React.useMemo(
		() => (quickOpen ? matchQuickReplies(p.quickReplies, body.slice(1)) : []),
		[quickOpen, p.quickReplies, body],
	);

	const pickQuickReply = (r) => {
		setBody(r.text);
		saveDraft(conversationId, r.text);
		requestAnimationFrame(() => ref.current?.focus());
	};

	const submit = async () => {
		const text = body.trim();
		if (!text || isLoading || !canType) return;
		const quoted = replyTo?.wamid ?? null;

		try {
			const result = await sendMessage({
				conversationId,
				// The key is stable across retries of THIS message.
				clientMessageId,
				type: 'text',
				text: { body: text },
				...(quoted ? { context: { messageId: quoted } } : {}),
			}).unwrap();

			setBody('');
			saveDraft.cancel();
			prefs.setDraft(conversationId, '');
			if (quoted && result?.wamid) prefs.rememberQuote(result.wamid, quoted);
			onCancelReply?.();
			// A new message needs a new key; reusing it would be seen as a
			// duplicate of the one just sent.
			setClientMessageId(newClientMessageId());
			ref.current?.focus();
		} catch (err) {
			notify.error('Message not sent', explainRefusal(err?.data));
		}
	};

	const onKeyDown = (e) => {
		if (quickOpen && quickMatches.length) {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setQrIndex((i) => (i + 1) % quickMatches.length);
				return;
			}
			if (e.key === 'ArrowUp') {
				e.preventDefault();
				setQrIndex((i) => (i - 1 + quickMatches.length) % quickMatches.length);
				return;
			}
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				pickQuickReply(quickMatches[qrIndex] ?? quickMatches[0]);
				return;
			}
			if (e.key === 'Tab') {
				e.preventDefault();
				pickQuickReply(quickMatches[qrIndex] ?? quickMatches[0]);
				return;
			}
		}
		if (e.key === 'Escape' && replyTo) {
			e.preventDefault();
			onCancelReply?.();
			return;
		}
		// Enter sends, Shift+Enter is a newline — the WhatsApp convention.
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			submit();
		}
	};

	// ── Attachments ──────────────────────────────────────────────────────
	const onFiles = async (files) => {
		const result = await attach(files[0]);
		if (!result.ok) notify.warning('Cannot attach', result.reason);
	};
	const { getRootProps, isDragActive } = useDropzone({
		noClick: true,
		noKeyboard: true,
		multiple: false,
		disabled: !canMedia,
		onDrop: (accepted) => accepted.length && onFiles(accepted),
	});
	const sendAttachment = async ({ caption }) => {
		const ok = await sendMedia({ file: attachment.file, kind: attachment.kind, caption, replyTo });
		if (ok) {
			clearAttachment();
			onCancelReply?.();
		}
	};

	// ── Voice notes ──────────────────────────────────────────────────────
	const startRecording = async () => {
		const ok = await recorder.start();
		if (!ok && recorder.error) notify.warning('Cannot record', recorder.error);
	};
	const finishRecording = async () => {
		const file = await recorder.stop();
		if (!file) return;
		const ok = await sendMedia({ file, kind: 'audio', replyTo });
		if (ok) onCancelReply?.();
	};

	// ★ Two different closed states. A chat WE started has no window yet, so
	// "reopen" would be a lie about a thread the agent just created — and this
	// is the very first thing they see after starting one.
	// ★ FIRST, BECAUSE IT OUTRANKS THE OTHERS.
	// The window being open or closed, and who holds the thread, are both beside
	// the point when this is not the number the lead has any more: a reply here
	// reaches whoever holds that number now, which may be a stranger.
	//
	// ⚠ A GUARDRAIL, NOT A RULE. The server still accepts the send, deliberately
	// — the thread is a real conversation with a real person, and only the LEAD
	// has moved on. Opened from the inbox, where no lead is in view, there is no
	// `numberState` and the composer behaves exactly as it always did.
	if (numberState?.isPreviousNumber) {
		return (
			<Blocked
				icon={FiClock}
				title="This is a previous number for this lead"
				detail={
					numberState.changedAt
						? `The lead's number changed on ${new Date(numberState.changedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}. Replying here still reaches whoever holds this one.`
						: 'Replying here still reaches whoever holds this number.'
				}
			/>
		);
	}

	if (!everInbound) {
		return (
			<Blocked
				icon={FiClock}
				title="Waiting for the first reply"
				detail="WhatsApp needs an approved template to open a conversation. Once they reply you can type freely for 24 hours."
				action={<SendTemplateButton conversationId={conversationId} />}
			/>
		);
	}

	if (!open) {
		return (
			<Blocked
				icon={FiClock}
				title="Outside the 24-hour window"
				detail="Only an approved template can reopen this conversation."
				action={<SendTemplateButton conversationId={conversationId} />}
			/>
		);
	}

	// ★ NO TEMPLATE BUTTON HERE, UNLIKE THE TWO STATES ABOVE.
	// Those two are closed by the 24-hour window, which a template is the one
	// thing that reopens. This one is closed by ownership, and ownership refuses
	// a template exactly as it refuses free text — the templates endpoint itself
	// answers NOT_CONVERSATION_OWNER. Offering the picker would open it onto a
	// refusal, which is the dead end this file exists to avoid. The way out is
	// Transfer or Claim, and the ownership bar above the thread already has it.
	if (notMine) {
		return (
			<Blocked
				icon={FiLock}
				title={`${conversation?.ownerUser?.name ?? 'Another agent'} is handling this conversation`}
				detail="Ask them to transfer it to you if you need to reply."
			/>
		);
	}

	const hasText = Boolean(body.trim());
	const ready = hasText && !isLoading && canType;
	const showMic = canMedia && !hasText;

	return (
		<Box
			{...getRootProps()}
			position="relative"
			px="3"
			py="2"
			bg="wa.composerBar"
			borderTopWidth="1px"
			borderColor="wa.divider"
			outline={isDragActive ? '2px dashed var(--chakra-colors-wa-accent)' : 'none'}
			outlineOffset="-6px"
		>
			{quickOpen && (
				<QuickReplyList matches={quickMatches} activeIndex={qrIndex} onPick={pickQuickReply} onManage={manager.onOpen} />
			)}

			<Flex align="flex-end">
				{recorder.recording ? (
					<VoiceRecorder
						seconds={recorder.seconds}
						level={recorder.level}
						onCancel={recorder.cancel}
						onSend={finishRecording}
						isSending={sendingMedia}
					/>
				) : (
					<>
						<HStack spacing="0" mb="1" mr="1">
							{canMedia && <AttachMenu onFiles={onFiles} isDisabled={preparing} />}
							{/* ★ THE TEMPLATE ICON SITS WHERE THE EMOJI PICKER DID, AND IS
							    ALWAYS AVAILABLE — including inside the 24-hour window,
							    where free text is allowed and a template is simply a
							    faster way to say something the business says often. It
							    self-gates on `message.send.template`, so an agent without
							    that grant sees the row it always had. */}
							<SendTemplateButton conversationId={conversationId} as="icon" />
							{/* The emoji picker stood here. It is gone deliberately: the
							    row has space for two icons, emoji can still be typed, and
							    an approved template is the thing an agent cannot do any
							    other way. */}
						</HStack>

						<Box
							flex="1"
							minW="0"
							bg="wa.input"
							borderRadius="24px"
							px="4"
							py="2"
							mr="2"
							transition="box-shadow 120ms ease"
							_focusWithin={{ boxShadow: '0 0 0 1px var(--chakra-colors-wa-accent)' }}
						>
							<AnimatePresence initial={false}>
								{replyTo && (
									<MotionBox
										key="reply"
										initial={slideUp.initial}
										animate={slideUp.animate}
										exit={slideUp.exit}
										transition={{ duration: dur(150), ease: EASE }}
										overflow="hidden"
									>
										<Box pt="1" pb="2">
											<QuotedMessage message={replyTo} wamid={replyTo.wamid} senderLabel={replySender} onClose={onCancelReply} compact />
										</Box>
									</MotionBox>
								)}
							</AnimatePresence>
							<Textarea
								ref={ref}
								value={body}
								onChange={onChange}
								onKeyDown={onKeyDown}
								placeholder="Type a message"
								aria-label="Message"
								rows={1}
								minH="unset"
								h={`${LINE_PX + 4}px`}
								lineHeight={`${LINE_PX}px`}
								fontSize="15px"
								color="wa.ink"
								_placeholder={{ color: 'wa.inkMuted' }}
								resize="none"
								variant="unstyled"
								p="0"
								isDisabled={!canType}
								overflowY="auto"
							/>
						</Box>
					</>
				)}

				{!recorder.recording && (
					<Box mb="0.5" flexShrink={0}>
						<AnimatePresence exitBeforeEnter initial={false}>
							{showMic ? (
								<CustomTooltip key="mic" label="Record a voice message" placement="top" hasArrow>
									<MotionIconButton
										aria-label="Record voice message"
										icon={<Icon as={FiMic} boxSize="24px" />}
										onClick={startRecording}
										borderRadius="full"
										size="lg"
										bg="wa.accent"
										color="wa.accentInk"
										_hover={{ bg: 'wa.accentHover' }}
										initial={{ scale: 0.6, rotate: -20, opacity: 0 }}
										animate={{ scale: 1, rotate: 0, opacity: 1 }}
										exit={{ scale: 0.6, rotate: 20, opacity: 0 }}
										transition={{ duration: dur(140), ease: EASE }}
									/>
								</CustomTooltip>
							) : (
								<CustomTooltip key="send" label={ready ? 'Send (Enter)' : 'Type a message to send'} placement="top" hasArrow>
									<MotionIconButton
										aria-label="Send message"
										icon={<Icon as={FiSend} boxSize="24px" />}
										onClick={submit}
										isLoading={isLoading}
										isDisabled={!ready}
										borderRadius="full"
										size="lg"
										bg="wa.accent"
										color="wa.accentInk"
										_hover={{ bg: 'wa.accentHover' }}
										_active={{ transform: 'scale(0.96)' }}
										_disabled={{ opacity: 0.5, cursor: 'not-allowed', bg: 'wa.accent' }}
										initial={{ scale: 0.6, rotate: 20, opacity: 0 }}
										animate={{ scale: 1, rotate: 0, opacity: 1 }}
										exit={{ scale: 0.6, rotate: -20, opacity: 0 }}
										transition={{ duration: dur(140), ease: EASE }}
									/>
								</CustomTooltip>
							)}
						</AnimatePresence>
					</Box>
				)}
			</Flex>

			<Text fontSize="0.65rem" color="wa.inkMuted" mt="1.5" ml="12" display={{ base: 'none', md: 'block' }}>
				Enter to send · Shift+Enter for a new line · / for quick replies
			</Text>

			<QuickReplyManager isOpen={manager.isOpen} onClose={manager.onClose} />
			<AttachmentPreview
				attachment={attachment}
				isOpen={Boolean(attachment)}
				onClose={clearAttachment}
				onSend={sendAttachment}
				isSending={sendingMedia}
			/>
		</Box>
	);
}
